import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield, Check, X, Loader2, AlertTriangle, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Moderation() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [flagged, setFlagged] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  useEffect(() => {
    if (user && (user.role === 'owner' || user.role === 'admin')) {
      fetchFlagged();
    }
  }, [user]);

  const fetchFlagged = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/flagged`, { withCredentials: true });
      setFlagged(data);
    } catch (err) {
      console.error('Error fetching flagged:', err);
    } finally {
      setLoading(false);
    }
  };

  const approve = async (id) => {
    setActing(id);
    try {
      await axios.post(`${API_URL}/api/admin/flagged/${id}/approve`, {}, { withCredentials: true });
      setFlagged(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setActing(null);
    }
  };

  const reject = async (id) => {
    if (!window.confirm('Reject and DELETE this character? This cannot be undone.')) return;
    setActing(id);
    try {
      await axios.post(`${API_URL}/api/admin/flagged/${id}/reject`, {}, { withCredentials: true });
      setFlagged(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setActing(null);
    }
  };

  if (!user || (user.role !== 'owner' && user.role !== 'admin')) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#71717A]">Admin access required</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24">
      <div className="sticky top-12 z-40 bg-[#050505] border-b border-white/5 px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-[#A1A1AA] hover:text-white" data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Shield className="w-5 h-5 text-[#E11D48]" />
          <div>
            <h1 className="text-lg font-bold text-white font-['Unbounded']">Moderation</h1>
            <p className="text-xs text-[#71717A]">{flagged.length} flagged characters</p>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#E11D48] animate-spin" />
          </div>
        ) : flagged.length === 0 ? (
          <div className="text-center py-16">
            <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="text-white font-medium">All clear!</p>
            <p className="text-[#71717A] text-sm mt-1">No flagged characters to review</p>
          </div>
        ) : (
          flagged.map((char) => (
            <div key={char.id} className="p-4 bg-[#0F1115] border border-[#E11D48]/20 rounded-xl" data-testid={`flagged-${char.id}`}>
              <div className="flex gap-3">
                {/* Avatar with blur */}
                <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 relative border border-white/10">
                  {char.avatar_url ? (
                    <>
                      <img src={char.avatar_url} alt={char.name} className="w-full h-full object-cover blur-lg" />
                      <button
                        onClick={(e) => {
                          const img = e.currentTarget.previousSibling;
                          img.classList.toggle('blur-lg');
                        }}
                        className="absolute inset-0 flex items-center justify-center bg-black/50 hover:bg-black/30 transition-colors"
                        data-testid={`reveal-${char.id}`}
                      >
                        <Eye className="w-5 h-5 text-white/70" />
                      </button>
                    </>
                  ) : (
                    <div className="w-full h-full bg-[#1A1D24] flex items-center justify-center text-[#71717A] text-xs">No img</div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-white line-clamp-1">{char.name}</h3>
                  <p className="text-xs text-[#71717A] mt-0.5">by {char.creator_name}</p>
                  <div className="flex items-center gap-1 mt-1 px-2 py-0.5 bg-[#E11D48]/10 border border-[#E11D48]/20 rounded-full w-fit">
                    <AlertTriangle className="w-3 h-3 text-[#E11D48]" />
                    <span className="text-[10px] text-[#E11D48] font-medium">{char.flag_reason}</span>
                  </div>
                  <p className="text-xs text-[#71717A] mt-1 line-clamp-2">{char.description}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  onClick={() => approve(char.id)}
                  disabled={acting === char.id}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs"
                  data-testid={`approve-${char.id}`}
                >
                  {acting === char.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => reject(char.id)}
                  disabled={acting === char.id}
                  className="flex-1 border-[#E11D48]/30 text-[#E11D48] hover:bg-[#E11D48]/10 rounded-lg text-xs"
                  data-testid={`reject-${char.id}`}
                >
                  <X className="w-3 h-3 mr-1" /> Reject & Delete
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
